// The painted textures in public/textures, loaded once and shared.
//
// Surfaces come in styles: each level picks one set of walls, one floor and
// one ceiling from its seed, and keeps them for that whole level.
import * as THREE from 'three';

export const WALL_SETS = {
  stone: ['wall_stone_a', 'wall_stone_b', 'wall_stone_c'],
  brick: ['wall_brick_a', 'wall_brick_b'],
  cavern: ['wall_cavern_a', 'wall_cavern_b'],
};

export const FLOORS = ['floor_stone', 'floor_tile', 'floor_cavern'];

// Ground that can be painted on a square at a time in the map editor. The
// dungeon's own three are in here too, so a painted map can use them.
export const CARPET = 'carpet';
export const PAINTABLE_FLOORS = [
  { id: 'floor_stone', name: 'Flagstone' },
  { id: 'floor_tile', name: 'Tiles' },
  { id: 'floor_cavern', name: 'Cave rock' },
  { id: 'floor_cobble', name: 'Cobbles' },
  { id: 'floor_grass', name: 'Grass' },
  { id: 'floor_dirt', name: 'Dirt' },
  { id: 'floor_temple', name: 'Temple marble' },
  { id: CARPET, name: 'Carpet' },
];
export const CEILINGS = ['ceiling_stone', 'ceiling_timber', 'ceiling_cavern'];

// The town is out of doors: house fronts, cobbles and sky. It is kept out of
// the sets above so that a dungeon level can never be built out of it.
const TOWN_WALLS = ['wall_town_plaster', 'wall_town_timber', 'wall_town_stone'];
export const TOWN_THEME = { walls: 'town', floor: 'floor_cobble', ceiling: 'ceiling_sky', outdoor: true };

const ALL_WALL_SETS = { ...WALL_SETS, town: TOWN_WALLS };

// Used before a run starts, and if a level ever arrives without a style
export const DEFAULT_THEME = { walls: 'stone', floor: FLOORS[0], ceiling: CEILINGS[0] };

// Props that look the same whatever the level is made of
const PROPS = ['door_wood', 'door_broken', 'chest_wood'];

// Textures ship as JPEG, which is a fifth of the size and indistinguishable on
// a wall. Only those with a cut-out hole need PNG's transparency.
const NEEDS_ALPHA = ['door_broken'];
const colourUrl = (name) => `./textures/${name}.${NEEDS_ALPHA.includes(name) ? 'png' : 'jpg'}`;
const normalUrl = (name) => `./textures/${name}_normal.jpg`;

const loader = new THREE.TextureLoader();
const cache = {};

// A texture and its normal map, loaded on first use
export function painted(name) {
  if (!cache[name]) {
    const map = loader.load(colourUrl(name));
    map.colorSpace = THREE.SRGBColorSpace;
    cache[name] = { map, normalMap: loader.load(normalUrl(name)) };
  }
  return cache[name];
}

export function wallsOf(theme) {
  return ALL_WALL_SETS[(theme || DEFAULT_THEME).walls] || WALL_SETS.stone;
}

export function wallMaps(theme, variant) {
  const set = wallsOf(theme);
  return painted(set[variant % set.length]);
}

// Which wall texture a square uses; fixed per square, so it never flickers
export function wallVariant(x, y) {
  return (x * 7 + y * 13) % 3;
}

// Which piece of carpet a square gets, given which of its neighbours are
// carpeted. A square with carpet all round is a middle piece; one open side
// is an edge, two sides meeting at a corner is a corner piece.
//
// A square on its own, or with carpet on only one side, is a rug: bordered
// all the way round. Two opposite sides open is a runner one square wide, for
// which there is no piece painted, so it borrows the nearest edge.
export function carpetPiece({ north, east, south, west }) {
  const open = [!north && 'n', !east && 'e', !south && 's', !west && 'w'].filter(Boolean);
  if (!open.length) return 'carpet_middle';
  if (open.length >= 3) return 'carpet_rug';
  if (open.length === 1) return `carpet_edge_${open[0]}`;

  const upDown = open.find((side) => side === 'n' || side === 's');
  const leftRight = open.find((side) => side === 'e' || side === 'w');
  if (upDown && leftRight) return `carpet_corner_${upDown}${leftRight}`;
  // Opposite sides only: lay it as an edge, which reads better than a corner
  return `carpet_edge_${open[0]}`;
}

// The ground a square is floored with: what the level is made of, unless the
// square has been painted with something of its own.
export function floorMaps(theme, override, carpeted) {
  if (override === CARPET) return painted(carpetPiece(carpeted || {}));
  return painted(override || (theme || DEFAULT_THEME).floor);
}

export function ceilingMaps(theme) {
  return painted((theme || DEFAULT_THEME).ceiling);
}

function load(name) {
  return Promise.all([colourUrl(name), normalUrl(name)].map((url) =>
    new Promise((resolve) => loader.load(url, resolve, undefined, resolve))));
}

// Wait for the props and the first style, then fetch the other styles quietly
// in the background: a run may never visit half of them.
// Every piece the carpet is laid from
export const CARPET_PIECES = [
  'carpet_middle',
  'carpet_rug',
  ...['n', 'e', 's', 'w'].map((side) => `carpet_edge_${side}`),
  ...['nw', 'ne', 'sw', 'se'].map((corner) => `carpet_corner_${corner}`),
];

export function preloadTextures() {
  // The town is the first thing anyone sees, so it loads with the props
  const core = [...PROPS, ...TOWN_WALLS, TOWN_THEME.floor, TOWN_THEME.ceiling,
                ...WALL_SETS[DEFAULT_THEME.walls], DEFAULT_THEME.floor, DEFAULT_THEME.ceiling];
  const rest = [
    ...Object.values(WALL_SETS).flat(), ...FLOORS, ...CEILINGS,
    // Painted maps may ask for any of these, but most runs never will
    ...PAINTABLE_FLOORS.map((floor) => floor.id).filter((id) => id !== CARPET),
    ...CARPET_PIECES,
  ].filter((name) => !core.includes(name));

  return Promise.all(core.map(load)).then(() => {
    core.forEach(painted);
    rest.forEach((name) => load(name).then(() => painted(name)));
  });
}
